import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import MainNavigation from '../../components/ui/MainNavigation';
import Footer from '../home-page/components/Footer';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import DisputeForm from './components/DisputeForm';
import ResolutionTracker from './components/ResolutionTracker';
import { violationChargeService } from '../../services/violationChargeService';
import { disputeService } from '../../services/disputeService';
import { supabase } from '../../lib/supabase';

const ChargeDispute = () => {
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const chargeId = searchParams?.get('chargeId');
  
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [charges, setCharges] = useState([]);
  const [isLoadingCharges, setIsLoadingCharges] = useState(false);
  const [activeView, setActiveView] = useState('select'); // 'select', 'form', 'tracking'
  const [disputeResponses, setDisputeResponses] = useState([]);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchCharges = async () => {
      if (!user?.id) return;

      try {
        setIsLoadingCharges(true);
        const data = await violationChargeService?.getRenterCharges();
        const disputableCharges = data?.filter(c => 
          c?.chargeStatus === 'pending' || c?.chargeStatus === 'disputed'
        );
        setCharges(disputableCharges || []);

        // If chargeId in URL, auto-select that charge
        if (chargeId && disputableCharges?.length > 0) {
          const charge = disputableCharges?.find(c => c?.id === chargeId);
          if (charge) {
            setSelectedCharge(charge);
            setActiveView(charge?.chargeStatus === 'disputed' ? 'tracking' : 'form');
            
            // Fetch dispute responses if disputed
            if (charge?.chargeStatus === 'disputed') {
              const responses = await disputeService?.getDisputesByCharge(charge?.id);
              setDisputeResponses(responses || []);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching charges:', error);
      } finally {
        setIsLoadingCharges(false);
      }
    };

    if (isAuthenticated) {
      fetchCharges();
    }
  }, [user, isAuthenticated, chargeId]);

  // Real-time subscription for dispute updates
  useEffect(() => {
    if (!user?.id || !selectedCharge) return;

    const channel = disputeService?.subscribeToDisputes(user?.id, async (payload) => {
      // Refresh dispute responses when updates occur
      if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE') {
        if (selectedCharge?.chargeStatus === 'disputed') {
          const responses = await disputeService?.getDisputesByCharge(selectedCharge?.id);
          setDisputeResponses(responses || []);
        }
      }
    });

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [user?.id, selectedCharge]);

  const handleChargeSelect = async (charge) => {
    setSelectedCharge(charge);
    setActiveView(charge?.chargeStatus === 'disputed' ? 'tracking' : 'form');
    
    // Fetch dispute responses if disputed
    if (charge?.chargeStatus === 'disputed') {
      try {
        const responses = await disputeService?.getDisputesByCharge(charge?.id);
        setDisputeResponses(responses || []);
      } catch (error) {
        console.error('Error fetching dispute responses:', error);
      }
    }
  };

  const handleDisputeSubmitted = async (updatedCharge) => {
    setSelectedCharge(updatedCharge);
    setCharges(prev => prev?.map(c => c?.id === updatedCharge?.id ? updatedCharge : c));
    setActiveView('tracking');
    
    // Send notification email
    try {
      await disputeService?.sendDisputeNotification(updatedCharge?.id, 'dispute_filed');
    } catch (error) {
      console.error('Error sending dispute notification:', error);
    }
    
    // Fetch dispute responses
    try {
      const responses = await disputeService?.getDisputesByCharge(updatedCharge?.id);
      setDisputeResponses(responses || []);
    } catch (error) {
      console.error('Error fetching dispute responses:', error);
    }
  };

  const handleBackToSelection = () => {
    setSelectedCharge(null);
    setActiveView('select');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="font-body text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <MainNavigation isAuthenticated={isAuthenticated} user={user} />
      
      <main className="flex-1 pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="AlertCircle" size={24} className="text-primary" strokeWidth={2} />
              </div>
              <div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
                  Charge Dispute Center
                </h1>
                <p className="font-body text-muted-foreground mt-1">
                  Appeal violation fees with evidence-based dispute resolution
                </p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          {activeView === 'select' && (
            <div className="space-y-6">
              {/* Info Banner */}
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
                <div className="flex items-start space-x-4">
                  <Icon name="Info" size={24} className="text-primary flex-shrink-0 mt-1" strokeWidth={2} />
                  <div>
                    <h3 className="font-body font-semibold text-foreground mb-2">
                      How Dispute Resolution Works
                    </h3>
                    <ul className="font-body text-sm text-foreground space-y-1">
                      <li>• Select a charge to dispute from your pending violations</li>
                      <li>• Provide detailed reason and upload supporting evidence</li>
                      <li>• Track your dispute through review, evaluation, and resolution stages</li>
                      <li>• Communicate directly with mediators throughout the process</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Charge Selection */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-4">
                  Select Charge to Dispute
                </h2>
                
                {isLoadingCharges ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="font-body text-muted-foreground">Loading charges...</p>
                  </div>
                ) : charges?.length === 0 ? (
                  <div className="bg-muted/30 rounded-lg p-12 text-center">
                    <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
                    <h3 className="font-body font-semibold text-foreground mb-2">
                      No Charges Available
                    </h3>
                    <p className="font-body text-sm text-muted-foreground mb-4">
                      You don't have any pending or disputed charges at this time.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => navigate('/renter-violation-charges')}
                      iconName="ArrowLeft"
                    >
                      View All Charges
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {charges?.map((charge) => (
                      <ChargeCard
                        key={charge?.id}
                        charge={charge}
                        onSelect={() => handleChargeSelect(charge)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeView === 'form' && selectedCharge && (
            <DisputeForm
              charge={selectedCharge}
              onBack={handleBackToSelection}
              onSubmit={handleDisputeSubmitted}
            />
          )}

          {activeView === 'tracking' && selectedCharge && (
            <ResolutionTracker
              charge={selectedCharge}
              onBack={handleBackToSelection}
            />
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

// Charge Card Component
const ChargeCard = ({ charge, onSelect }) => {
  const statusConfig = {
    pending: { label: 'Pending Payment', color: 'bg-warning/10 text-warning border-warning/20', icon: 'Clock' },
    disputed: { label: 'Under Dispute', color: 'bg-primary/10 text-primary border-primary/20', icon: 'AlertCircle' },
  };

  const config = statusConfig?.[charge?.chargeStatus] || statusConfig?.pending;

  return (
    <div className="bg-card border border-border rounded-lg p-6 hover:shadow-elevation-2 transition-smooth">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <h3 className="font-body font-semibold text-foreground">
              {charge?.chargeReference}
            </h3>
            <div className={`flex items-center space-x-1 px-2 py-1 rounded-full border ${config?.color}`}>
              <Icon name={config?.icon} size={12} strokeWidth={2} />
              <span className="font-caption text-xs font-semibold">{config?.label}</span>
            </div>
          </div>
          <p className="font-caption text-sm text-muted-foreground">
            {charge?.violationReason}
          </p>
        </div>
      </div>

      <div className="space-y-3 mb-4">
        <div className="flex items-center justify-between">
          <span className="font-caption text-sm text-muted-foreground">Charge Amount</span>
          <span className="font-body text-lg font-bold text-foreground">
            ${charge?.chargeAmount?.toFixed(2)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="font-caption text-sm text-muted-foreground">Issued Date</span>
          <span className="font-body text-sm text-foreground">
            {new Date(charge?.createdAt)?.toLocaleDateString()}
          </span>
        </div>
        {charge?.disputeDate && (
          <div className="flex items-center justify-between">
            <span className="font-caption text-sm text-muted-foreground">Disputed On</span>
            <span className="font-body text-sm text-foreground">
              {new Date(charge?.disputeDate)?.toLocaleDateString()}
            </span>
          </div>
        )}
      </div>

      <Button
        variant={charge?.chargeStatus === 'disputed' ? 'outline' : 'primary'}
        className="w-full"
        onClick={onSelect}
        iconName={charge?.chargeStatus === 'disputed' ? 'Eye' : 'FileText'}
      >
        {charge?.chargeStatus === 'disputed' ? 'View Dispute Status' : 'File Dispute'}
      </Button>
    </div>
  );
};

export default ChargeDispute;